import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dimmer,
  Dropdown,
  Grid,
  Icon,
  Input,
  Loader,
  Modal,
  Popup
} from 'semantic-ui-react';
import { useHistory } from 'react-router-dom';

import { FieldLabel } from '../../../../../../../styles';
import {
  maskCpfOrCnpj,
  maskPhoneNumber,
  maskZipCode,
  numberToMoneyString
} from '../../../../../../../services/masks';
import {
  CoveragesRow,
  HeaderAndDivider,
  HeaderSubtitle,
  QuotationContainer,
  QuotationHeader,
  QuotationList,
  QuotationResultContainer,
  QuotationValue
} from './styles';
import {
  warningNotification,
  successNotification
} from '../../../../../../../services/notification';
import { YelumProposalFormProps } from '../../interfaces/yelum.proposal.interface';
import { getDataByDocument } from '../../../../../../../services/documents';
import moment from 'moment-timezone';
import { AddressService } from '../../../../../../../services/address';
import { validPhone } from '../../../../../../../utils/validPhone';
import { post } from '../../../../../../../services/request';
import YelumSummaryModal from './SummaryModal';
import { BudgetContext } from '../..';
import LenderInsuranceAddon from '../../../../../../../components/LenderInsuranceAddon';
import { useSelector } from 'react-redux';
import { iRootState } from '../../../../../../../store';
import {
  applyYelumCompetenceToFormValues,
  getCompetenceFlowLabel,
  inferYelumCompetenceFromDate,
  YelumCompetence,
} from '../../../../../../../utils/yelumCompetence';
import FirePropertyWalletSearchDropdown, {
  FireWalletSelection
} from '../../../../components/FirePropertyWalletSearchDropdown';

interface YelumApiResponse {
  quoteNo?: number;
  quoteYear?: number;
  proposalNo?: number;
  liquidPremium?: number;
  totalPremium?: number;
  monthlyPremium?: number;
  iof?: number;
  additionalPremium?: number;
  referenceCode?: string;
  fireInsuranceAnalysisId?: string;
  fireInsuranceId?: string;
  installments?: Array<{
    installmentNo: number;
    totalPremium: number;
  }>;
}

const ControlledField = ({
  label,
  value
}: {
  label: string;
  value: string | number | undefined;
}) => {
  return (
    <>
      <FieldLabel>{label}</FieldLabel>
      <span>{value}</span>
    </>
  );
};

const YelumCreatePolicyModal = (props: YelumProposalFormProps) => {
  const {
    open,
    onClose,
    data: propsData,
    estateId,
    context,
    fireInsuranceAnalysisId
  } = props;

  const history = useHistory();
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState(propsData);
  const [isHiring, setIsHiring] = useState<boolean>(false);
  const [isContracting, setIsContracting] = useState<boolean>(false);
  const [tenantCellphone, setTenantCellphone] = useState<string | undefined>(
    props.initialTenant?.phoneNumber ?? undefined
  );
  const [addressNumber, setAddressNumber] = useState<string>('');
  const [notAddressNumber, setNotAddressNumber] = useState<boolean>(false);
  const [addressComplement, setAddressComplement] = useState<string | undefined>(
    undefined
  );
  const [zipCode, setZipCode] = useState<string | undefined>(
    props.initialAddress?.zipCode ?? undefined
  );
  const [city, setCity] = useState<string | undefined>(
    props.initialAddress?.city ?? undefined
  );
  const [state, setState] = useState<string | undefined>(
    props.initialAddress?.state ?? undefined
  );
  const [district, setDistrict] = useState<string | undefined>(
    props.initialAddress?.neighborhood ?? undefined
  );
  const [street, setStreet] = useState<string | undefined>(
    props.initialAddress?.street ?? undefined
  );
  const [beneficiaryDocument, setBeneficiaryDocument] = useState<string | undefined>(
    props.initialInsured?.document ?? undefined
  );
  const [beneficiaryName, setBeneficiaryName] = useState<string | undefined>(
    props.initialInsured?.name ?? undefined
  );
  const [beneficiaryCellphone, setBeneficiaryCellphone] = useState<string | undefined>(
    props.initialInsured?.phoneNumber ?? undefined
  );

  const [loadingTenantDocument, setLoadTenantDocument] = useState<boolean>(false);
  const [loadingBeneficiaryDocument, setLoadBeneficiaryDocument] =
    useState<boolean>(false);
  const [tenantDocument, setTenantDocument] = useState<string | undefined>(
    props.initialTenant?.document ?? undefined
  );
  const [tenantName, setTenantName] = useState<string | undefined>(
    props.initialTenant?.name ?? undefined
  );
  const [zipCodeLoading, setZipCodeLoading] = useState<boolean>(false);
  const [reference, setReference] = useState<string>('');
  const [walletSelection, setWalletSelection] = useState<FireWalletSelection | null>(null);

  const addressDisabled = Boolean(walletSelection?.propertyPublicId);

  const handleWalletSelect = (selection: FireWalletSelection | null) => {
    setWalletSelection(selection);

    if (!selection) return;

    const { address } = selection;
    setZipCode(address.zipCode ?? undefined);
    setCity(address.city ?? undefined);
    setState(address.state ?? undefined);
    setDistrict(address.neighborhood ?? undefined);
    setStreet(address.street ?? undefined);
    if (address.number) {
      setAddressNumber(address.number);
      setNotAddressNumber(false);
    } else {
      setAddressNumber('');
    }
    setAddressComplement(address.complement ?? undefined);
  };

  useEffect(() => {
    if (!props.initialAddress) return;

    setZipCode(props.initialAddress.zipCode ?? undefined);
    setCity(props.initialAddress.city ?? undefined);
    setState(props.initialAddress.state ?? undefined);
    setDistrict(props.initialAddress.neighborhood ?? undefined);
    setStreet(props.initialAddress.street ?? undefined);
    if (props.initialAddress.number) {
      setAddressNumber(props.initialAddress.number);
      setNotAddressNumber(false);
    }
    if (props.initialAddress.complement) {
      setAddressComplement(props.initialAddress.complement);
    }
  }, [props.initialAddress]);

  const [showSummaryModal, setShowSummaryModal] = useState<boolean>(false);
  const [summaryData, setSummaryData] = useState<YelumApiResponse | undefined>(undefined);
  const [previousQuotationData, setPreviousQuotationData] = useState<
    YelumApiResponse | undefined
  >(undefined);

  const lenderInsuranceEnabled = useSelector(
    (state: iRootState) => state.availableBrokersAndEstates.lenderInsuranceEnabled ?? false
  );
  const lenderInsuranceRate = useSelector(
    (state: iRootState) => state.availableBrokersAndEstates.lenderInsuranceRate
  );
  const [includeLenderInsurance, setIncludeLenderInsurance] = useState<boolean>(false);

  const loadDataForEdit = () => {
    if (!propsData) return;
    const isEditContext =
      (propsData as any)?.isEdit || (propsData as any)?.context === 'EDITING';

    if (!isEditContext) {
      console.log('Não é contexto de edição, mantendo campos vazios');
      return;
    }
    if ((propsData as any)?.savedTenantName) {
      setTenantName((propsData as any).savedTenantName);
    }
    if ((propsData as any)?.savedTenantDocument) {
      setTenantDocument((propsData as any).savedTenantDocument);
    }
    if ((propsData as any)?.savedTenantCellphone) {
      setTenantCellphone((propsData as any).savedTenantCellphone);
    }

    if ((propsData as any)?.savedPropertyOwnerName) {
      setBeneficiaryName((propsData as any).savedPropertyOwnerName);
    }
    if ((propsData as any)?.savedPropertyOwnerDocument) {
      setBeneficiaryDocument((propsData as any).savedPropertyOwnerDocument);
    }
    if ((propsData as any)?.savedPropertyOwnerCellphone) {
      setBeneficiaryCellphone((propsData as any).savedPropertyOwnerCellphone);
    }
    const savedProperty = (propsData as any)?.savedProperty;
    if (savedProperty) {
      setZipCode(savedProperty.zipcode);
      setStreet(savedProperty.street);
      setDistrict(savedProperty.district);
      setCity(savedProperty.city);
      setState(savedProperty.state);
      setAddressNumber(savedProperty.number || '');
      setAddressComplement(savedProperty.complement || '');
      setReference((propsData as any)?.reference || '');
    }

    // Verificar se não tem número
    if (!savedProperty?.number || savedProperty.number === 'S/N') {
      setNotAddressNumber(true);
      setAddressNumber('');
    }
  };

  const onBlurDocument = async (document: string, step = 'tenant') => {
    if (!document) return;

    try {
      const unformattedDoc = document.replace(/\D/g, '');
      const data = await getDataByDocument(unformattedDoc);

      if (step === 'tenant') {
        setLoadTenantDocument(true);
        setTenantName(data?.name ?? data?.company ?? '');
        setTenantCellphone(data?.phone ? validPhone(data?.phone) : '');
      } else {
        setLoadBeneficiaryDocument(true);
        setBeneficiaryName(data?.name ?? data?.company ?? '');
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoadTenantDocument(false);
      setLoadBeneficiaryDocument(false);
    }
  };

  const onChangeZipCode = (zipCode: string): void => {
    setZipCodeLoading(true);
    const maskedValue = maskZipCode(zipCode);
    setZipCode(maskedValue);
    if (maskedValue.length >= 9) {
      AddressService.getAddressByZipCode(zipCode)
        .then(address => {
          setCity(address.city ?? '');
          setDistrict(address.district ?? '');
          setState(address.state ?? '');
          setStreet(address.street ?? '');
        })
        .catch(() => {
          warningNotification('CEP invalido!', 'Por favor, digite um CEP valido.');
        });
    }

    setZipCodeLoading(false);
  };

  const validateFields = (): boolean => {
    if (!tenantDocument || tenantDocument.replace(/\D/g, '').length < 11) {
      warningNotification(
        'CPF/CNPJ do Segurado obrigatório',
        'Por favor, informe um CPF/CNPJ válido para o segurado.'
      );
      return false;
    }

    if (!tenantName || tenantName.trim().length < 3) {
      warningNotification(
        'Nome do Segurado obrigatório',
        'Por favor, informe o nome completo do segurado.'
      );
      return false;
    }

    if (!tenantCellphone || tenantCellphone.replace(/\D/g, '').length < 10) {
      warningNotification(
        'Telefone obrigatório',
        'Por favor, informe um telefone válido para o segurado.'
      );
      return false;
    }

    if (!beneficiaryDocument || beneficiaryDocument.replace(/\D/g, '').length < 11) {
      warningNotification(
        'CPF/CNPJ do Beneficiário obrigatório',
        'Por favor, informe um CPF/CNPJ válido para o beneficiário.'
      );
      return false;
    }

    if (!beneficiaryCellphone || beneficiaryCellphone.replace(/\D/g, '').length < 10) {
      warningNotification(
        'Telefone obrigatório',
        'Por favor, informe um telefone válido para o beneficiário.'
      );
      return false;
    }

    if (!beneficiaryName || beneficiaryName.trim().length < 3) {
      warningNotification(
        'Nome do Beneficiário obrigatório',
        'Por favor, informe o nome completo do beneficiário.'
      );
      return false;
    }

    if (!zipCode || zipCode.replace(/\D/g, '').length !== 8) {
      warningNotification('CEP obrigatório', 'Por favor, informe um CEP válido.');
      return false;
    }

    if (!street || street.trim().length < 3) {
      warningNotification('Logradouro obrigatório', 'Por favor, informe o logradouro.');
      return false;
    }

    if (!district || district.trim().length < 2) {
      warningNotification('Bairro obrigatório', 'Por favor, informe o bairro.');
      return false;
    }

    if (!city || city.trim().length < 2) {
      warningNotification('Cidade obrigatória', 'Por favor, informe a cidade.');
      return false;
    }

    if (!state || state.trim().length !== 2) {
      warningNotification('UF obrigatório', 'Por favor, informe o estado (UF).');
      return false;
    }

    if (!notAddressNumber && (!addressNumber || addressNumber.trim().length === 0)) {
      warningNotification(
        'Número do endereço obrigatório',
        'Por favor, informe o número ou marque "Não possui número".'
      );
      return false;
    }

    return true;
  };

  const buildPayload = () => {
    if (!data) {
      throw new Error('Dados do orçamento não encontrados');
    }

    const periodSource =
      (data as any).startVigency || data.policyPeriodStart || data.policyPeriodEnd;
    const competence =
      (data as any).competence ||
      inferYelumCompetenceFromDate(periodSource) ||
      YelumCompetence.CURRENT_MONTH;
    const competenceDates = applyYelumCompetenceToFormValues(competence);
    const startDate = moment(competenceDates.startVigency).format('YYYY-MM-DD');
    const endDate = moment(competenceDates.endVigency).format('YYYY-MM-DD');
    const monthsValidity = moment(endDate).diff(moment(startDate), 'months') || 12;

    return {
      isEdit: [BudgetContext.detail, BudgetContext.editing].includes(context),
      estateId: estateId || '',
      fireInsuranceAnalysisId: fireInsuranceAnalysisId,
      property_public_id: walletSelection?.propertyPublicId,
      data: {
        property: {
          streetType: street ? street.split(' ')[0] : '',
          street: street || '',
          number: notAddressNumber ? 'S/N' : addressNumber || '',
          complement: addressComplement ? addressComplement.substring(0, 16) : '',
          district: district || '',
          city: city || '',
          state: state?.toUpperCase() || '',
          zipcode: zipCode?.replace(/\D/g, '') || '',
          reference: reference
        },

        tenantName: tenantName || '',
        tenantDocument: tenantDocument?.replace(/\D/g, '') || '',
        tenantCellphone: tenantCellphone?.replace(/\D/g, '') || '',

        propertyOwnerName: beneficiaryName || '',
        propertyOwnerDocument: beneficiaryDocument?.replace(/\D/g, '') || '',
        propertyOwnerCellphone: beneficiaryCellphone?.replace(/\D/g, '') || '',

        rentValue: Number(data.rentValue) || 0,
        competence,
        startDate: startDate,
        endDate: endDate,
        monthsValidity: monthsValidity,
        installments: 1,
        occupancyName: data.propertyType,
        commission: Number(data.brokerCommission) || 0, // Comissão da corretora (Broker)
        prolabore: Number(data.commissionTax) || 0, // Comissão da imobiliária (Pro Labore)
        coverageType: data.coverageType,
        clientInstallments: data.clientInstallments || data.installments,
        // Valores calculados do motor de cálculo (sempre trabalhar com MENSAL)
        calculatedTotalPremium: data.calculationResult?.totalMonthly || 0, // Valor MENSAL (Yelum cobra mensalmente)
        calculatedIof: data.calculationResult?.iofMonthly || 0, // IOF MENSAL
        calculatedMonthlyPremium: data.calculationResult?.totalMonthly || 0, // Valor mensal (redundante mas mantido por compatibilidade)
        coverages: (() => {
          // Incluir coberturas regulares
          console.log(
            '[Yelum Modal buildPayload] data.coverages original:',
            data.coverages
              ?.filter(c => c.checked)
              .map(c => ({
                code: c.coverage?.code,
                name: c.coverage?.name
              }))
          );

          const regularCoverages =
            data.coverages
              ?.filter(c => c.checked)
              ?.map(c => ({
                id: c.coverage?.id,
                value: Number(c.value) || 0,
                name: c.coverage?.name
              })) || [];

          // Adicionar cobertura de incêndio baseada no tipo selecionado
          // Agora aceita qualquer prefixo de código, apenas verifica a parte final (E10, E11, E12)
          const fireCoverageMap: { [key: string]: string } = {
            'Prédio e Conteúdo': 'E10',
            Prédio: 'E11',
            Conteúdo: 'E12'
          };

          const fireCoverageSuffix = fireCoverageMap[data.coverageType];

          if (fireCoverageSuffix && data.fireInsuranceValue) {
            // Buscar a cobertura de incêndio correta pelo sufixo do código E propertyType
            const fireCoverage = data.allCoverages?.find(fc => {
              if (!fc.coverage.code) return false;
              // Extrai a parte após o hífen e compara com o sufixo esperado
              const codeParts = fc.coverage.code.split('-');
              const codeMatches =
                codeParts.length > 1 && codeParts[1] === fireCoverageSuffix;

              // IMPORTANTE: Verificar se o propertyType corresponde
              const propertyMatches = fc.propertyType === data.propertyType;

              return codeMatches && propertyMatches;
            });

            console.log(
              '[Yelum Modal] Cobertura de incêndio encontrada:',
              fireCoverage
                ? {
                    id: fireCoverage.coverage.id,
                    name: fireCoverage.coverage.name,
                    code: fireCoverage.coverage.code,
                    propertyType: fireCoverage.propertyType
                  }
                : 'NÃO ENCONTRADA'
            );

            if (fireCoverage) {
              regularCoverages.unshift({
                id: fireCoverage.coverage.id,
                value: Number(data.fireInsuranceValue) || 0,
                name: fireCoverage.coverage.name
              });
            }
          }

          // Adicionar assistência se existir (assistência é tratada como cobertura na API Yelum)
          console.log('[Yelum Modal] Assistência no data:', data.assistance);
          console.log('[Yelum Modal] Assistência ID:', data.assistanceId);
          console.log('[Yelum Modal] Total allCoverages:', data.allCoverages?.length);

          if (data.assistanceId) {
            // Buscar a assistência em allCoverages usando o ID
            const assistanceCoverage = data.allCoverages?.find(
              ac => ac.coverage.id === data.assistanceId
            );

            console.log('[Yelum Modal] Assistência encontrada:', assistanceCoverage);

            if (!assistanceCoverage) {
              console.warn(
                '[Yelum Modal] ⚠️ Assistência não encontrada em allCoverages. IDs disponíveis:',
                data.allCoverages?.map(ac => ({
                  id: ac.coverage.id,
                  name: ac.coverage.name
                }))
              );
            }

            if (assistanceCoverage) {
              regularCoverages.push({
                id: assistanceCoverage.coverage.id,
                value: 0, // Assistências geralmente não têm valor monetário
                name: assistanceCoverage.coverage.name
              });
            }
          }

          console.log(
            '[Yelum Modal] Coberturas finais:',
            regularCoverages.map(c => ({ id: c.id, name: c.name, value: c.value }))
          );

          return regularCoverages;
        })(),
        lenderInsurance: includeLenderInsurance
      }
    };
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      return;
    }

    try {
      setIsHiring(true);
      setLoading(true);

      const previousData: YelumApiResponse = {
        totalPremium: data?.totalPremium || 0,
        liquidPremium: (data as any)?.liquidPremium || 0,
        iof: (data as any)?.iof || 0,
        additionalPremium: (data as any)?.additionalPremium || 0,
        monthlyPremium: (data as any)?.calculationResult?.totalMonthly || 0
      };
      setPreviousQuotationData(previousData);

      const payload = buildPayload();

      console.log('Payload para salvar orçamento:', JSON.stringify(payload));

      const response = await post(
        '/fire-insurance-quotations/yelum-seguros/budget?save=true',
        payload
      );

      if (response.status === 200 && response.data) {
        successNotification(
          'Orçamento salvo com sucesso!',
          'Redirecionando para a listagem de orçamentos...'
        );

        // Fechar modal
        onClose();

        // Redirecionar para tela de orçamentos após um pequeno delay
        setTimeout(() => {
          history.push('/admin/fire-insurance-budgets');
        }, 1000);
      } else {
        successNotification(
          'Orçamento salvo com sucesso!',
          'Redirecionando para a listagem de orçamentos...'
        );

        onClose();

        setTimeout(() => {
          history.push('/admin/fire-insurance-budgets');
        }, 1000);
      }
    } catch (error) {
      // post() lança response.data diretamente em erros 4xx/5xx; para erros de rede lança AxiosError
      const errorBody = (error as any)?.response?.data ?? (error as any);
      const status = (error as any)?.response?.status ?? errorBody?.statusCode;
      const errorMessage =
        errorBody?.message || (error as any)?.message || 'Erro desconhecido.';

      if (errorBody?.error === 'Yelum API Error') {
        warningNotification('Erro na API Yelum', errorMessage);
      } else if (status === 400) {
        warningNotification(
          'Dados inválidos',
          'Verifique se todos os campos obrigatórios estão preenchidos corretamente.'
        );
      } else if (status === 401) {
        warningNotification(
          'Erro de autenticação',
          'Problema na autenticação com a seguradora. Tente novamente.'
        );
      } else if (status === 500) {
        warningNotification(
          'Erro interno',
          'Erro interno do servidor. Nossa equipe foi notificada.'
        );
      } else {
        warningNotification(
          'Erro ao salvar orçamento',
          `Não foi possível salvar o orçamento: ${errorMessage}`
        );
      }
    } finally {
      setIsHiring(false);
      setLoading(false);
    }
  };

  const handleHire = async () => {
    if (!validateFields()) {
      return;
    }

    try {
      setIsContracting(true);
      setLoading(true);

      const payload = buildPayload();

      console.log('Payload para contratar:', JSON.stringify(payload));

      const saveResponse = await post(
        '/fire-insurance-quotations/yelum-seguros/budget?save=true',
        payload
      );

      const savedAnalysisId = saveResponse?.data?.fireInsuranceAnalysisId;

      if (!savedAnalysisId) {
        warningNotification(
          'Erro ao salvar',
          'O orçamento foi salvo mas não foi possível obter o identificador necessário para contratação. Acesse a listagem de cotações salvas para contratar.'
        );
        onClose();
        setTimeout(() => {
          history.push('/admin/fire-insurance-budgets');
        }, 1500);
        return;
      }

      try {
        const hireResponse = await post('/fire-insurance-proposals/hire/yelum', {
          fireInsuranceAnalysisId: savedAnalysisId
        });

        const hireCompetence =
          (data as any)?.competence ||
          inferYelumCompetenceFromDate(
            (data as any)?.startVigency || data?.policyPeriodStart || '',
          );
        const isScheduledHire = hireCompetence === YelumCompetence.NEXT_MONTH;

        if (hireResponse?.pending) {
          warningNotification(
            'Contratação Solicitada',
            hireResponse.message ||
              'A contratação foi enfileirada e será processada quando o lote for regularizado.'
          );
          onClose();
          setTimeout(() => {
            history.push('/admin/fire-insurance-budgets');
          }, 1000);
        } else {
          successNotification(
            isScheduledHire ? 'Contratação agendada!' : 'Seguro contratado com sucesso!',
            isScheduledHire
              ? hireResponse?.message ||
                  'A apólice será processada automaticamente na data de vigência.'
              : 'Redirecionando para a listagem de apólices...'
          );
          onClose();
          setTimeout(() => {
            history.push('/admin/fire-insurance-policies');
          }, 1000);
        }
      } catch (hireError) {
        const errorBody = (hireError as any)?.response?.data ?? (hireError as any);
        const status = (hireError as any)?.response?.status ?? errorBody?.statusCode;
        const errorMessage =
          errorBody?.message ||
          (hireError as any)?.message ||
          'Erro ao contratar o seguro.';

        if (status === 400) {
          const errorCode = errorBody?.errorCode || (() => {
            try {
              const parsed = JSON.parse(errorBody?.message || errorMessage || '{}');
              return parsed?.errorCode || null;
            } catch {
              return null;
            }
          })();

          if (errorCode === 'ESTATE_NO_POLICIES') {
            warningNotification(
              'Competência indisponível',
              'Competência do mês atual não disponível para imobiliárias novas. Selecione o próximo mês.'
            );
          } else if (errorCode === 'RETROACTIVITY_EXCEEDED') {
            warningNotification(
              'Janela de retroatividade excedida',
              errorMessage || 'Item não pode ser adicionado: ultrapassa janela de retroatividade. Selecione a competência do próximo mês.'
            );
          } else {
            warningNotification(
              'Competência indisponível',
              errorMessage || 'Verifique os dados e tente novamente.'
            );
          }
        } else if (status === 403) {
          warningNotification('Contratação não permitida', errorMessage);
        } else if (status === 404) {
          warningNotification(
            'Orçamento não encontrado',
            'O orçamento salvo não foi encontrado. Entre em contato com o suporte.'
          );
        } else if (status === 500) {
          warningNotification(
            'Erro interno',
            'Erro interno ao contratar o seguro. Nossa equipe foi notificada.'
          );
        } else {
          warningNotification(
            'Erro na contratação',
            `Não foi possível contratar o seguro: ${errorMessage}`
          );
        }

        onClose();
        setTimeout(() => {
          history.push('/admin/fire-insurance-budgets');
        }, 1500);
      }
    } catch (saveError) {
      const errorBody = (saveError as any)?.response?.data ?? (saveError as any);
      const status = (saveError as any)?.response?.status ?? errorBody?.statusCode;
      const errorMessage =
        errorBody?.message || (saveError as any)?.message || 'Erro desconhecido.';

      if (errorBody?.error === 'Yelum API Error') {
        warningNotification('Erro na API Yelum', errorMessage);
      } else if (status === 400) {
        warningNotification(
          'Dados inválidos',
          'Verifique se todos os campos obrigatórios estão preenchidos corretamente.'
        );
      } else if (status === 401) {
        warningNotification(
          'Erro de autenticação',
          'Problema na autenticação com a seguradora. Tente novamente.'
        );
      } else if (status === 500) {
        warningNotification(
          'Erro interno',
          'Erro interno do servidor. Nossa equipe foi notificada.'
        );
      } else {
        warningNotification(
          'Erro ao salvar orçamento',
          `Não foi possível salvar o orçamento: ${errorMessage}`
        );
      }
      // Modal permanece aberto para que o usuário corrija os dados
    } finally {
      setIsContracting(false);
      setLoading(false);
    }
  };

  // useEffect para carregar dados quando modal abre no contexto de edição
  useEffect(() => {
    if (open && propsData) {
      loadDataForEdit();
    }
  }, [open, propsData]);

  return (
    <Modal open={open} onClose={onClose} closeOnEscape={false} closeOnDimmerClick={false}>
      <Modal.Header
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        Orçamento de Seguro Incêndio
      </Modal.Header>
      <Modal.Content scrolling>
        {loading && (
          <Dimmer active={loading} inverted>
            <Loader />
          </Dimmer>
        )}

        {data && (
          <Grid style={{ padding: 5 }} columns={'equal'}>
            <HeaderAndDivider>Resumo do Orçamento</HeaderAndDivider>

            <Grid.Row>
              <Grid.Column>
                <ControlledField label={'Tipo de Cobertura'} value={data.coverageType} />
              </Grid.Column>
              <Grid.Column>
                <ControlledField label={'Tipo do Imóvel'} value={data.propertyType} />
              </Grid.Column>
              <Grid.Column>
                <ControlledField label={'Assistência'} value={data.assistance} />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column>
                <ControlledField
                  label={'Valor do Aluguel'}
                  value={
                    data.rentValue ? numberToMoneyString(Number(data.rentValue)) : ''
                  }
                />
              </Grid.Column>
              <Grid.Column>
                <ControlledField
                  label={'Competência'}
                  value={(() => {
                    const competence =
                      (data as any).competence ||
                      inferYelumCompetenceFromDate(
                        (data as any).startVigency || data.policyPeriodStart,
                      );
                    const startLabel = (data as any).startVigency
                      ? moment((data as any).startVigency).format('DD/MM/YYYY')
                      : data.policyPeriodStart
                        ? moment(data.policyPeriodStart).format('DD/MM/YYYY')
                        : '';
                    const flowLabel = competence
                      ? getCompetenceFlowLabel(competence)
                      : '';
                    return `${startLabel}${flowLabel ? ` — ${flowLabel}` : ''}`;
                  })()}
                />
              </Grid.Column>
              <Grid.Column>
                <ControlledField
                  label={'Final da Vigência'}
                  value={
                    (data as any).endVigency
                      ? moment((data as any).endVigency).format('DD/MM/YYYY')
                      : data.policyPeriodEnd
                  }
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column>
                <ControlledField label={'PL(%)'} value={data.commissionTax} />
              </Grid.Column>
              <Grid.Column>
                {(data as any).hasDiscount && (data as any).discount && (
                  <ControlledField
                    label={'Desconto Aplicado'}
                    value={`${(data as any).discount}%`}
                  />
                )}
              </Grid.Column>
            </Grid.Row>

            <HeaderAndDivider>
              Dados do Segurado
              <HeaderSubtitle>(Inquilino ou Proprietário)</HeaderSubtitle>
            </HeaderAndDivider>

            <Grid.Row>
              <Grid.Column width={5}>
                <FieldLabel paddingBottom>CPF/CNPJ</FieldLabel>
                <Input
                  fluid
                  name={'tenantDocument'}
                  placeholder={'Digite o documento...'}
                  value={tenantDocument ? maskCpfOrCnpj(tenantDocument) : undefined}
                  onChange={e => setTenantDocument(e.target.value)}
                  onBlur={() => onBlurDocument(tenantDocument || '', 'tenant')}
                />
              </Grid.Column>

              <Grid.Column width={6}>
                <FieldLabel paddingBottom>NOME COMPLETO</FieldLabel>
                <Input
                  fluid
                  maxLength="40"
                  name={'tenantName'}
                  placeholder={'Nome completo do segurado...'}
                  value={tenantName}
                  loading={loadingTenantDocument}
                  onChange={e => setTenantName(e.target.value)}
                />
              </Grid.Column>

              <Grid.Column width={5}>
                <FieldLabel paddingBottom>TELEFONE</FieldLabel>
                <Input
                  fluid
                  name={'tenantCellphone'}
                  placeholder={'(11) 99999-9999'}
                  value={tenantCellphone ? maskPhoneNumber(tenantCellphone) : ''}
                  loading={loadingTenantDocument}
                  onChange={e => {
                    const value = validPhone(e.target.value);
                    setTenantCellphone(value);
                  }}
                />
              </Grid.Column>
            </Grid.Row>

            <HeaderAndDivider>
              Dados do Beneficiário
              <HeaderSubtitle>(Inquilino ou Proprietário)</HeaderSubtitle>
            </HeaderAndDivider>

            <Grid.Row>
              <Grid.Column width={5}>
                <FieldLabel paddingBottom>CPF/CNPJ</FieldLabel>
                <Input
                  fluid
                  name={'beneficiaryDocument'}
                  placeholder={'Digite o documento...'}
                  value={
                    beneficiaryDocument ? maskCpfOrCnpj(beneficiaryDocument) : undefined
                  }
                  onChange={e => setBeneficiaryDocument(e.target.value)}
                  onBlur={() => onBlurDocument(beneficiaryDocument || '', 'beneficiary')}
                />
              </Grid.Column>

              <Grid.Column width={6}>
                <FieldLabel paddingBottom>NOME COMPLETO</FieldLabel>
                <Input
                  fluid
                  maxLength="40"
                  name={'beneficiaryName'}
                  placeholder={'Nome completo do beneficiário...'}
                  value={beneficiaryName}
                  loading={loadingBeneficiaryDocument}
                  onChange={e => setBeneficiaryName(e.target.value)}
                />
              </Grid.Column>

              <Grid.Column width={5}>
                <FieldLabel paddingBottom>TELEFONE</FieldLabel>
                <Input
                  fluid
                  name={'beneficiaryCellphone'}
                  placeholder={'(11) 99999-9999'}
                  value={
                    beneficiaryCellphone ? maskPhoneNumber(beneficiaryCellphone) : ''
                  }
                  loading={loadingBeneficiaryDocument}
                  onChange={e => {
                    const value = validPhone(e.target.value);
                    setBeneficiaryCellphone(value);
                  }}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid>
              <FirePropertyWalletSearchDropdown
                estateId={estateId}
                value={walletSelection}
                onSelect={handleWalletSelect}
              />
            </Grid>

            <HeaderAndDivider>Endereço do Imóvel Segurado</HeaderAndDivider>

            <Grid.Row>
              <Grid.Column width={4}>
                <FieldLabel paddingBottom>CEP</FieldLabel>
                <Input
                  fluid
                  name={'zipcode'}
                  placeholder={'00000-000'}
                  value={zipCode}
                  disabled={addressDisabled}
                  onChange={e => {
                    const value = maskZipCode(e.target.value);
                    onChangeZipCode(value);
                    setZipCode(value);
                  }}
                  loading={zipCodeLoading}
                />
              </Grid.Column>

              <Grid.Column width={8}>
                <FieldLabel paddingBottom>LOGRADOURO</FieldLabel>
                <Input
                  fluid
                  name={'street'}
                  placeholder={'Nome da rua, avenida, etc...'}
                  value={street}
                  disabled={addressDisabled || zipCodeLoading}
                  onChange={e => setStreet(e.target.value)}
                  loading={zipCodeLoading}
                />
              </Grid.Column>

              <Grid.Column width={4}>
                <FieldLabel paddingBottom>BAIRRO</FieldLabel>
                <Input
                  fluid
                  name={'district'}
                  placeholder={'Bairro'}
                  value={district}
                  disabled={addressDisabled || zipCodeLoading}
                  onChange={e => setDistrict(e.target.value)}
                  loading={zipCodeLoading}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={6}>
                <FieldLabel paddingBottom>CIDADE</FieldLabel>
                <Input
                  fluid
                  name={'city'}
                  placeholder={'Cidade'}
                  value={city}
                  disabled={addressDisabled || zipCodeLoading}
                  onChange={e => setCity(e.target.value)}
                  loading={zipCodeLoading}
                />
              </Grid.Column>

              <Grid.Column width={2}>
                <FieldLabel paddingBottom>UF</FieldLabel>
                <Input
                  fluid
                  name={'state'}
                  placeholder={'SP'}
                  value={state}
                  disabled={addressDisabled || zipCodeLoading}
                  onChange={e => setState(e.target.value)}
                  loading={zipCodeLoading}
                />
              </Grid.Column>

              <Grid.Column width={4}>
                <FieldLabel paddingBottom>NÚMERO</FieldLabel>
                <Input
                  fluid
                  type="text"
                  disabled={addressDisabled || notAddressNumber}
                  name={'addressNumber'}
                  placeholder={!notAddressNumber ? 'Número' : 'S/N'}
                  value={addressNumber}
                  onChange={e => {
                    const numericValue = String(e.target.value).replace(/[^0-9]/g, '');
                    setAddressNumber(numericValue);
                  }}
                  loading={zipCodeLoading}
                />
              </Grid.Column>

              <Grid.Column verticalAlign="middle" width={4}>
                <Checkbox
                  label="Não possui número (S/N)"
                  checked={notAddressNumber}
                  onChange={(e: any, { checked }) => {
                    if (checked) {
                      setNotAddressNumber(true);
                    } else {
                      setNotAddressNumber(false);
                    }
                    setAddressNumber('');
                  }}
                  style={{ paddingTop: '25px' }}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={8}>
                <FieldLabel paddingBottom>COMPLEMENTO</FieldLabel>
                <Input
                  fluid
                  maxLength="16"
                  name={'addressComplement'}
                  placeholder={'Apartamento, bloco, sala, etc... (opcional)'}
                  value={addressComplement}
                  onChange={e => setAddressComplement(e.target.value)}
                  loading={zipCodeLoading}
                />
              </Grid.Column>

              <Grid.Column width={8}>
                <FieldLabel paddingBottom>CÓDIGO DE REFERÊNCIA</FieldLabel>
                <Input
                  fluid
                  maxLength="15"
                  name={'reference'}
                  placeholder={'Código personalizado (opcional)'}
                  value={reference}
                  onChange={e => {
                    if (e.target.value.match('^[a-zA-Z0-9]*$')) {
                      setReference(e.target.value);
                    }
                  }}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        )}

        {lenderInsuranceEnabled && tenantDocument?.replace(/\D/g, '').length === 11 && (
          <Grid style={{ padding: 5 }} columns={'equal'}>
            <Grid.Column width={16}>
              <LenderInsuranceAddon
                rateValue={lenderInsuranceRate}
                selected={includeLenderInsurance}
                onSelect={(selected: boolean) => setIncludeLenderInsurance(selected)}
              />
            </Grid.Column>
          </Grid>
        )}

        <QuotationContainer>
          <QuotationHeader>
            <h3>
              <Icon name="shield" /> Orçamento de Seguro Incêndio
            </h3>
          </QuotationHeader>
          <QuotationResultContainer>
            <QuotationList>
              <>
                {/* Cobertura de Incêndio (sempre primeiro) */}
                {(data as any)?.fireInsuranceValue && (
                  <CoveragesRow key="fire-insurance">
                    <div>
                      <span className={'full-insurance-name'}>
                        <input type="checkbox" disabled checked />
                        INCÊNDIO / EXPLOSÃO / FUMAÇA / Q. AERONAVES
                      </span>
                    </div>
                    <div>
                      <span className="insurance-value">
                        {numberToMoneyString((data as any).fireInsuranceValue)}
                      </span>
                    </div>
                  </CoveragesRow>
                )}

                {/* Outras coberturas */}
                {(data as any)?.coverages
                  ?.filter((c: any) => c.checked)
                  ?.map((c: any, index: number) => (
                    <CoveragesRow key={c.coverage?.CoverCodeLiberty || index}>
                      <div>
                        <span className={'full-insurance-name'}>
                          <input type="checkbox" disabled checked />
                          {c.coverage?.name || c.name || 'Cobertura'}
                        </span>
                      </div>

                      <div>
                        <span className="insurance-value">
                          {numberToMoneyString(c.value)}
                        </span>
                      </div>
                    </CoveragesRow>
                  ))}
              </>
            </QuotationList>
            {(() => {
              const totalPremium = data?.totalPremium || 0;
              const monthlyPremium =
                (data as any)?.calculationResult?.totalMonthly || totalPremium / 12;
              const installments = (data as any)?.installments || 12;
              const formattedPremium = numberToMoneyString(totalPremium);
              const formattedMonthlyPremium = numberToMoneyString(monthlyPremium);

              const hasYelumData = (data as any)?.quoteNo && (data as any)?.referenceCode;

              return (
                <QuotationValue>
                  <h3 style={{ fontSize: '20px' }}>
                    {hasYelumData ? 'Cotação Yelum (Valores Reais)' : 'Valor do Seguro'}
                  </h3>

                  {hasYelumData && (
                    <div
                      style={{ marginBottom: '10px', fontSize: '12px', color: '#666' }}
                    >
                      <div>
                        Nº Cotação: {(data as any).quoteNo}/{(data as any).quoteYear}
                      </div>
                      <div>Referência: {(data as any).referenceCode}</div>
                    </div>
                  )}

                  <label
                    style={{ fontSize: '13px', paddingBottom: '10px', display: 'block' }}
                  >
                    Valor Mensal: {formattedMonthlyPremium}
                  </label>

                  {installments !== 12 && (
                    <label
                      style={{
                        fontSize: '13px',
                        paddingBottom: '10px',
                        display: 'block'
                      }}
                    >
                      Valor Parcelamento: {installments}x{' '}
                      {numberToMoneyString(
                        Number(((monthlyPremium * 12) / installments).toFixed(2))
                      )}
                    </label>
                  )}

                  {(data as any)?.liquidPremium && (
                    <small style={{ fontSize: '12px', color: '#666' }}>
                      Prêmio líquido: {numberToMoneyString((data as any).liquidPremium)}
                    </small>
                  )}

                  {(data as any)?.iof && (
                    <small style={{ fontSize: '12px', color: '#666', display: 'block' }}>
                      IOF: {numberToMoneyString((data as any).iof)}
                    </small>
                  )}

                  {hasYelumData && (
                    <>
                      <div
                        style={{ marginTop: '10px', fontSize: '11px', color: '#28a745' }}
                      >
                        ✓ Valores confirmados pela seguradora
                      </div>

                      {(data as any).installments &&
                        (data as any).installments.length > 0 && (
                          <div style={{ marginTop: '10px' }}>
                            <small
                              style={{
                                fontSize: '11px',
                                color: '#666',
                                fontWeight: 'bold'
                              }}
                            >
                              Plano de parcelas:
                            </small>
                            <div
                              style={{
                                fontSize: '10px',
                                color: '#666',
                                maxHeight: '60px',
                                overflowY: 'auto'
                              }}
                            >
                              {(data as any).installments
                                .slice(0, 3)
                                .map((installment: any, index: number) => (
                                  <div key={index}>
                                    {installment.installmentNo}ª:{' '}
                                    {numberToMoneyString(
                                      Number(installment.totalPremium)
                                    )}
                                  </div>
                                ))}
                              {(data as any).installments.length > 3 && (
                                <div>
                                  ... e mais {(data as any).installments.length - 3}{' '}
                                  parcelas
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                    </>
                  )}
                </QuotationValue>
              );
            })()}
          </QuotationResultContainer>
        </QuotationContainer>
      </Modal.Content>

      <Modal.Actions>
        <Button
          icon
          disabled={isHiring || isContracting}
          labelPosition={'left'}
          onClick={() => onClose()}
        >
          <Icon name={'arrow left'} />
          Voltar
        </Button>

        <Popup
          content={
            'Salva o orçamento para análise posterior. Você será redirecionado para a listagem de orçamentos e poderá realizar a contratação quando desejar.'
          }
          position={'top center'}
          trigger={
            <Button
              positive
              icon
              labelPosition={'right'}
              onClick={handleSubmit}
              loading={isHiring}
              disabled={isHiring || isContracting}
            >
              <Icon name={'checkmark'} />
              Salvar Orçamento
            </Button>
          }
        />

        <Popup
          content={
            'Efetiva a contratação do seguro imediatamente. O orçamento será salvo e você será redirecionado para a listagem de apólices.'
          }
          position={'top center'}
          trigger={
            <Button
              color={'blue'}
              icon
              labelPosition={'right'}
              onClick={handleHire}
              loading={isContracting}
              disabled={isHiring || isContracting}
            >
              <Icon name={'handshake'} />
              Contratar
            </Button>
          }
        />
      </Modal.Actions>

      <YelumSummaryModal
        open={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        data={summaryData}
        previousData={previousQuotationData}
        quotationData={{
          tenantName,
          tenantDocument,
          tenantCellphone,
          beneficiaryName,
          beneficiaryDocument,
          propertyType: data?.propertyType,
          rentValue: data?.rentValue ? Number(data.rentValue) : undefined,
          coverageType: data?.coverageType,
          policyPeriodStart: data?.policyPeriodStart,
          policyPeriodEnd: data?.policyPeriodEnd,
          assistance: data?.assistance,
          commissionTax: data?.commissionTax ? Number(data.commissionTax) : undefined,
          propertyZipcode: zipCode,
          propertyStreet: street,
          propertyNumber: addressNumber,
          propertyComplement: addressComplement,
          propertyDistrict: district,
          propertyCity: city,
          propertyState: state
        }}
      />
    </Modal>
  );
};

export default YelumCreatePolicyModal;
